/* eslint-disable no-console */
/* eslint-disable max-len */
import {Collapse, Flex} from 'antd';
import React from 'react';
import {useNavigate} from 'react-router-dom';

import PCFStep1 from '../../assets/PCFStep1.svg';
import PCFStep2 from '../../assets/PCFStep2.svg';
import PCFStep3 from '../../assets/PCFStep3.svg';
import PCFStep4 from '../../assets/PCFStep4.svg';
import PCFStep5 from '../../assets/PCFStep5.svg';
import PageContent from '../../components/PageContent/PageContent';
import {allRoutes} from '../../constants/Routes';

import StartPCFButton from './utils/StartPCFButton';

const STEPS = [
  {
    num: 1,
    title: 'Goal & Scope Definition',
    description: 'Define why, for whom, and what boundaries your study covers.',
    icon: <img src={PCFStep1} alt="Goal and scope definition" />,
    side: 'right',
    path: allRoutes.PCF.goalAndScope
  },
  {
    num: 2,
    title: 'LCI Data Collection',
    description:
      'Enter materials, energy, transport and waste data for each life cycle stage.',
    icon: <img src={PCFStep2} alt="LCI data collection" />,
    side: 'left',
    path: allRoutes.PCF.lciDataCollection
  },
  {
    num: 3,
    title: 'Impact Configuration',
    description:
      'Access actionable steps, customized to your company’s maturity profile, highlighting key areas for improvement.',
    icon: <img src={PCFStep3} alt="Impact configuration" />,
    side: 'right',
    path: allRoutes.PCF.impactConfiguration
  },
  {
    num: 4,
    title: 'Results & Interpretation',
    description:
      'View PCF result, hotspot breakdown, and write your interpretation.',
    icon: <img src={PCFStep4} alt="Results and interpretation" />,
    side: 'left',
    path: allRoutes.PCF.resultsInterpretation
  },
  {
    num: 5,
    title: 'Report & Export',
    description:
      'Generate an ISO 14067 compliant PDF, CSV, JSON or Excel report.',
    icon: <img src={PCFStep5} alt="Report and export" />,
    side: 'right',
    path: allRoutes.PCF.reportExport
  }
];

const INFO_CARDS = [
  {
    title: 'WHAT YOU’LL DO',
    items: [
      'Define study name, product, and reporting organisation',
      'Set functional unit — what the product does and how much',
      'Choose system boundary: Cradle-to-Gate or Cradle-to-Grave',
      'Select the PCF standard and declare intended application'
    ]
  },
  {
    title: 'WHAT YOU NEED READY',
    items: [
      'Product name and internal SKU or product code',
      'Functional unit description (what the product does)',
      'Reporting organisation name',
      'HSN / CN code for the product'
    ]
  }
];

const {Panel} = Collapse;

const FAQ_ITEMS = [
  {
    q: 'How long does a PCF study take to complete?',
    a: 'A first-time PCF study typically takes 45–90 minutes if you have your data ready. The most time-consuming part is Step 2 (LCI Data Collection), where you enter materials, energy, and transport. If you need to gather data from suppliers or internal systems first, plan for 1–3 days of data collection before starting. The platform auto-saves your progress at every step so you can pause and resume at any point.'
  },
  {
    q: 'Do I need a third-party verifier to use this?',
    a: 'No, you can complete a PCF study and use the results internally without third-party verification. However, if you intend to make public claims, publish to an EPD program, or comply with regulations like CBAM, third-party verification is recommended or required. The platform supports both internal-use and externally-verified workflows.'
  },
  {
    q: 'How is PCF different from the Carbon Accounting module?',
    a: 'Carbon Accounting tracks your organisation’s overall greenhouse gas emissions across Scopes 1, 2, and 3. PCF (Product Carbon Footprint) zooms into a single product and quantifies its emissions across its full life cycle. Use Carbon Accounting for corporate reporting, and PCF when you need product-level data for customers, EPDs, or design decisions.'
  },
  {
    q: 'What if I don’t have all the data ready?',
    a: 'You can still proceed. The platform allows you to use industry-average values from Ecoinvent and the Bharat Carbon India database when primary data is unavailable. Each row in the inventory captures the data source, so your final report transparently shows which figures are primary, secondary, or estimated.'
  },
  {
    q: 'What is the difference between a PCF and an EPD?',
    a: 'A PCF (Product Carbon Footprint) measures only greenhouse gas emissions, expressed as kg CO₂e. An EPD (Environmental Product Declaration) is a broader, third-party-verified document that covers multiple environmental impact categories — climate change, acidification, eutrophication, water use, and more. A PCF is a subset of an EPD’s climate-change category.'
  },
  {
    q: 'What standards does this module comply with?',
    a: 'The PCF module is built around ISO 14067:2018 as the primary methodology, with full alignment to GHG Protocol Product Standard, PEFCR rules where applicable, and the IPCC AR6 GWP100 characterization factors. Outputs can be tailored for ISO 14067, GHG Protocol, or EPD/PCR submission depending on your selected report type.'
  }
];

const renderIcon = (isActive: boolean) => {
  return isActive ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="2"
      viewBox="0 0 14 2"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 0L0 2L14 2V4.76837e-07L0 0Z"
        fill="white"
      />
    </svg>
  ) : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8 0L6 0V6H0L0 8H6L6 14H8L8 8H14V6L8 6V0Z"
        fill="white"
      />
    </svg>
  );
};

const PCFPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PageContent noPadding>
      <Flex vertical>
        <Flex vertical align="center" justify="center" className="lp-hero">
          <Flex
            vertical
            align="center"
            justify="center"
            gap={20}
            className="lp-hero-content"
          >
            <div className="lp-hero-eyebrow">PRODUCT CARBON FOOTPRINT</div>
            <h1 className="lp-hero-title">
              Make your Product <em>compliance ready</em>!!!
            </h1>
            <p className="lp-hero-subtitle">
              A guided 5-step workflow to quantify, analyse, and report the
              <br />
              greenhouse gas emissions of any product - ISO 14067:2018
              compliant.
            </p>
            <StartPCFButton />
          </Flex>
        </Flex>

        {/* ===== Journey Section (heading) ===== */}
        <Flex
          vertical
          align="center"
          justify="center"
          className="lp-journey-section"
        >
          <Flex vertical align="center" justify="center" gap={10}>
            <div className="lp-eyebrow">THE 5-STEP PROCESS</div>
            <div className="lp-section-title">Your PCF calculation journey</div>
            <div className="lp-section-subtitle">
              Take our structured 5-step journey to evaluate your product’s
              carbon footprint.
            </div>
          </Flex>

          {/* ===== Vertical Timeline ===== */}
          <Flex
            vertical
            align="center"
            justify="center"
            className="lp-timeline"
          >
            <div className="lp-timeline-line" />
            {STEPS.map((step) => (
              <div
                key={step.num}
                className={`lp-timeline-row lp-side-${step.side}`}
              >
                {/* LEFT cell */}
                <div className="lp-timeline-cell lp-cell-left">
                  {step.side === 'left' ? (
                    <Flex
                      vertical
                      align="center"
                      className="lp-step-content"
                      gap={10}
                      onClick={() => {
                        navigate(step.path);
                      }}
                    >
                      <Flex
                        className="lp-step-num"
                        vertical
                        align="center"
                        justify="center"
                      >
                        {step.num}
                      </Flex>
                      <div className="lp-step-title">{step.title}</div>
                      <div className="lp-step-desc">{step.description}</div>
                    </Flex>
                  ) : (
                    <div className="lp-step-icon-wrap">{step.icon}</div>
                  )}
                </div>

                {/* CENTER stepper cell */}
                <div className="lp-timeline-stepper">
                  <div className="lp-stepper-line-segment" />
                </div>

                {/* RIGHT cell */}
                <div className="lp-timeline-cell lp-cell-right">
                  {step.side === 'right' ? (
                    <Flex
                      vertical
                      align="center"
                      className="lp-step-content"
                      gap={10}
                      onClick={() => {
                        navigate(step.path);
                      }}
                    >
                      <Flex
                        className="lp-step-num"
                        vertical
                        align="center"
                        justify="center"
                      >
                        {step.num}
                      </Flex>
                      <div className="lp-step-title">{step.title}</div>
                      <div className="lp-step-desc">{step.description}</div>
                    </Flex>
                  ) : (
                    <div className="lp-step-icon-wrap">{step.icon}</div>
                  )}
                </div>
              </div>
            ))}
            <div className="lp-timeline-end-dot" />
          </Flex>
        </Flex>

        {/* ===== Journey lists in light gray panel ===== */}
        <Flex
          vertical
          align="center"
          justify="center"
          gap={30}
          className="lp-journey-panel"
        >
          <Flex vertical align="center" justify="center" gap={10}>
            <div className="lp-eyebrow">THE 5-STEP PROCESS</div>
            <div className="lp-section-title">Your PCF calculation journey</div>
            <div className="lp-section-subtitle">
              Take our structured 5-step journey to evaluate your product’s
              carbon footprint.
            </div>
          </Flex>

          <Flex vertical={false} className="lp-info-grid" gap={20}>
            {INFO_CARDS.map((card, index) => (
              <Flex
                key={index}
                vertical
                align="flex-start"
                className="lp-info-card"
              >
                <div className="lp-info-title">{card.title}</div>

                <ul className="lp-info-list">
                  {card.items.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </ul>
              </Flex>
            ))}
          </Flex>

          <div className="lp-tip-banner">
            💡 The functional unit is the most important decision in your study
            — it is the reference basis for every calculation that follows.
          </div>
        </Flex>

        {/* ===== FAQs ===== */}
        <Flex
          vertical
          align="center"
          justify="center"
          className="lp-faqs"
          gap={40}
        >
          <Flex vertical align="center" justify="center" gap={10}>
            <div className="lp-section-title-center">
              Frequently Asked Questions (FAQs)
            </div>
            <div className="lp-section-subtitle-center">
              Common questions about Product Carbon Footprints
            </div>
          </Flex>

          <Collapse
            className="lp-faq-collapse"
            accordion
            bordered={false}
            expandIcon={({isActive}) => (
              <Flex
                align="center"
                justify="center"
                className="lp-faq-collapse-icon"
              >
                {renderIcon(isActive ?? false)}
              </Flex>
            )}
            expandIconPosition="end"
          >
            {FAQ_ITEMS.map((item, idx) => (
              <Panel header={item.q} key={idx}>
                <div>{item.a}</div>
              </Panel>
            ))}
          </Collapse>
        </Flex>

        {/* ===== Bottom CTA banner ===== */}
        <Flex
          vertical
          align="center"
          justify="center"
          gap={20}
          className="lp-bottom-cta"
        >
          <div className="lp-bottom-cta-title">Ready to get started?</div>
          <div className="lp-bottom-cta-subtitle">
            Your progress is auto-saved. You can pause and resume any time.
          </div>
          <StartPCFButton />
        </Flex>
      </Flex>
    </PageContent>
  );
};

export default PCFPage;
